import { apiClient as axios } from '../lib/authClient';

const API_URL = `/push-tokens`;

/**
 * Register FCM token for push notifications
 * @param {string} token - FCM registration token
 * @param {string} platform - Platform (web, ios, android)
 * @returns {Promise<Object>} Response
 */
export const registerToken = async (token, deviceType = 'web', deviceName = '') => {
    try {
        const response = await axios.post(`${API_URL}/register`, {
            token,
            device_type: deviceType,
            device_name: deviceName
        });
        return response.data;
    } catch (error) {
        console.error('Error registering push token:', error);
        throw error;
    }
};

/**
 * Unregister/Remove FCM token
 * @param {string} token - FCM registration token to remove
 * @returns {Promise<Object>} Response
 */
export const unregisterToken = async (token) => {
    try {
        const response = await axios.post(`${API_URL}/unregister`, {
            token
        });
        return response.data;
    } catch (error) {
        console.error('Error unregistering push token:', error);
        throw error;
    }
};

/**
 * Get all registered devices for the authenticated user
 * @returns {Promise<Object>} Response with devices array
 */
export const getDevices = async () => {
    try {
        const response = await axios.get(`${API_URL}/devices`);
        if (response.data.status) {
            return response.data.data;
        }
        throw new Error(response.data.message || 'Failed to fetch devices');
    } catch (error) {
        console.error('Error fetching devices:', error);
        throw error;
    }
};

/**
 * Toggle a device's active status
 * @param {number} deviceId - Device ID
 * @param {boolean} isActive - New active state
 * @returns {Promise<Object>} Response
 */
export const toggleDevice = async (deviceId, isActive) => {
    try {
        const response = await axios.put(`${API_URL}/${deviceId}/toggle`, {
            is_active: isActive
        });
        return response.data;
    } catch (error) {
        console.error('Error toggling device:', error);
        throw error;
    }
};

/**
 * Delete a registered device
 * @param {number} deviceId - Device ID
 * @returns {Promise<Object>} Response
 */
export const deleteDevice = async (deviceId) => {
    try {
        const response = await axios.delete(`${API_URL}/${deviceId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting device:', error);
        throw error;
    }
};

/**
 * Remove all inactive (muted) devices
 * @returns {Promise<Object>} Response with deleted count
 */
export const cleanupInactive = async () => {
    try {
        const response = await axios.delete(`${API_URL}/cleanup-inactive`);
        return response.data;
    } catch (error) {
        console.error('Error cleaning up inactive devices:', error);
        throw error;
    }
};

export default {
    registerToken,
    unregisterToken,
    getDevices,
    toggleDevice,
    deleteDevice,
    cleanupInactive
};
